/**
 * UpdateAvailableNotice (bug 2026-10-27) — báo "Có phiên bản mới — Tải lại".
 *
 * Bối cảnh: vite-plugin-pwa `registerType: "autoUpdate"` → SW mới
 * skipWaiting + clientsClaim, nhưng tab đang mở VẪN chạy JS của bản cũ cho tới
 * khi reload. Người dùng thấy tính năng mới "không chạy" (vd bài khối ffuture
 * chỉ hiện video). Component này KHÔNG tự reload — học viên có thể đang làm
 * bài thi — mà hiện banner để người dùng chủ động bấm "Tải lại".
 *
 * Tín hiệu có bản mới:
 *   1. `controllerchange` khi trang đã có controller từ trước (lần cài SW đầu
 *      tiên cũng fire event này → bỏ qua).
 *   2. `vite:preloadError` — chunk lazy của bản cũ đã bị gỡ khỏi server/cache.
 * Kiểm tra chủ động: `registration.update()` mỗi 30 phút + khi tab hiện lại.
 *
 * Mount 1 lần ở root (main.tsx) để phủ cả trang học viên, admin, login.
 */

import { useEffect, useState } from "react";
import { Button } from "antd";
import { ReloadOutlined } from "@ant-design/icons";

const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

export function UpdateAvailableNotice() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const show = () => setVisible(true);

    const onPreloadError = (event: Event) => {
      event.preventDefault();
      show();
    };
    window.addEventListener("vite:preloadError", onPreloadError);

    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    if (!sw) {
      return () => window.removeEventListener("vite:preloadError", onPreloadError);
    }

    const hadController = !!sw.controller;
    const onControllerChange = () => {
      if (hadController) show();
    };
    sw.addEventListener("controllerchange", onControllerChange);

    const checkForUpdate = () => {
      sw.getRegistration()
        .then((reg) => reg?.update())
        .catch(() => undefined); // offline / SW bị chặn — bỏ qua
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };
    const timer = window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.removeEventListener("vite:preloadError", onPreloadError);
      sw.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: "50%",
        bottom: 24,
        transform: "translateX(-50%)",
        zIndex: 2000,
        width: "min(440px, calc(100vw - 32px))",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 16px",
        background: "#1f3a68",
        color: "#fff",
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
      }}
    >
      <span style={{ flex: 1, lineHeight: 1.4 }}>
        <strong>Có phiên bản mới.</strong> Tải lại trang để dùng bản mới nhất. Nếu đang làm bài thi,
        hãy nộp bài trước.
      </span>
      <Button type="primary" icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
        Tải lại
      </Button>
    </div>
  );
}
